import * as XLSX from 'xlsx';

export const formatExportCurrency = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(amount);
  } catch (e) {
    return `${currency} ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
  }
};

export const formatExportDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const EXPORT_TYPE_LABELS = {
  bank_balance: 'Bank Balance', fixed_deposit: 'Fixed Deposit',
  mutual_fund: 'Mutual Fund', treasury_bill: 'Treasury Bill',
  quoted_stocks: 'Quoted Stocks', government_bond: 'Government Bond',
  corporate_bond: 'Corporate Bond', private_equity: 'Private Equity',
  convertible: 'Convertible', safe: 'SAFE Investment', crypto: 'Cryptocurrency'
};

export const buildCsvContent = (investments, portfolioSummary) => {
  const headers = ['Name', 'Type', 'Amount Invested', 'Current Value', 'Profit/Loss', 'Return %', 'Currency', 'Created Date', 'Notes'];
  const rows = investments.map(inv => {
    const profitLoss = (inv.current_value || inv.amount) - inv.amount;
    const returnPct = ((profitLoss / inv.amount) * 100).toFixed(2);
    return [
      `"${inv.name || '-'}"`, `"${EXPORT_TYPE_LABELS[inv.type] || inv.type}"`,
      inv.amount || 0, inv.current_value || inv.amount || 0,
      profitLoss.toFixed(2), returnPct, inv.currency || 'USD',
      formatExportDate(inv.created_at), `"${inv.notes || '-'}"`
    ].join(',');
  });
  const summaryRow = [
    '"TOTAL"', '""', portfolioSummary?.total_invested || 0,
    portfolioSummary?.current_value || 0, portfolioSummary?.profit_loss || 0,
    portfolioSummary?.profit_loss_percentage?.toFixed(2) || 0, '""', '""', '""'
  ].join(',');
  return [headers.join(','), ...rows, '', summaryRow].join('\n');
};

export const buildPdfHtml = (investments, portfolioSummary) => {
  const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const investmentRows = investments.map(inv => {
    const profitLoss = (inv.current_value || inv.amount) - inv.amount;
    const returnPct = ((profitLoss / inv.amount) * 100).toFixed(2);
    const isProfit = profitLoss >= 0;
    return `<tr>
      <td><strong>${inv.name || '-'}</strong></td>
      <td><span class="type-badge">${EXPORT_TYPE_LABELS[inv.type] || inv.type}</span></td>
      <td style="text-align:right;">${formatExportCurrency(inv.amount, inv.currency)}</td>
      <td style="text-align:right;">${formatExportCurrency(inv.current_value || inv.amount, inv.currency)}</td>
      <td style="text-align:right;" class="${isProfit ? 'profit' : 'loss'}">${isProfit ? '+' : ''}${formatExportCurrency(profitLoss, inv.currency)}</td>
      <td style="text-align:right;" class="${isProfit ? 'profit' : 'loss'}">${isProfit ? '+' : ''}${returnPct}%</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>MyInvestFolio Portfolio Report</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;padding:40px;color:#1e293b;line-height:1.6}
.header{border-bottom:3px solid #10b981;padding-bottom:20px;margin-bottom:30px}
.header h1{font-size:32px;color:#0f172a;margin-bottom:5px}
.header .subtitle{color:#64748b;font-size:14px}
.summary{background:#f1f5f9;padding:20px;border-radius:8px;margin-bottom:30px}
.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:15px;margin-top:15px}
.summary-item{background:white;padding:15px;border-radius:6px}
.summary-item .label{font-size:12px;color:#64748b;text-transform:uppercase;letter-spacing:.5px}
.summary-item .value{font-size:24px;font-weight:bold;color:#0f172a;margin-top:5px}
.summary-item.profit .value{color:#10b981}
.summary-item.loss .value{color:#ef4444}
table{width:100%;border-collapse:collapse;margin-top:20px}
th{background:#0f172a;color:white;padding:12px;text-align:left;font-size:12px;text-transform:uppercase;letter-spacing:.5px}
td{padding:12px;border-bottom:1px solid #e2e8f0;font-size:14px}
tr:hover{background:#f8fafc}
.type-badge{background:#e0f2fe;color:#0369a1;padding:4px 8px;border-radius:4px;font-size:11px;font-weight:600}
.profit{color:#10b981;font-weight:600}
.loss{color:#ef4444;font-weight:600}
.footer{margin-top:40px;padding-top:20px;border-top:1px solid #e2e8f0;text-align:center;color:#64748b;font-size:12px}
@media print{body{padding:20px}.no-print{display:none}}
</style></head><body>
<div class="header"><h1>MyInvestFolio</h1><p class="subtitle">Portfolio Report - ${reportDate}</p></div>
<div class="summary"><h2 style="margin-bottom:15px;font-size:20px;">Portfolio Summary</h2>
<div class="summary-grid">
<div class="summary-item"><div class="label">Total Invested</div><div class="value">${formatExportCurrency(portfolioSummary?.total_invested || 0)}</div></div>
<div class="summary-item"><div class="label">Current Value</div><div class="value">${formatExportCurrency(portfolioSummary?.current_value || 0)}</div></div>
<div class="summary-item ${(portfolioSummary?.profit_loss || 0) >= 0 ? 'profit' : 'loss'}"><div class="label">Profit/Loss</div><div class="value">${formatExportCurrency(portfolioSummary?.profit_loss || 0)}</div></div>
<div class="summary-item ${(portfolioSummary?.profit_loss_percentage || 0) >= 0 ? 'profit' : 'loss'}"><div class="label">Return %</div><div class="value">${(portfolioSummary?.profit_loss_percentage || 0).toFixed(2)}%</div></div>
</div></div>
<h2 style="margin-bottom:15px;font-size:20px;">Investment Details</h2>
<table><thead><tr><th>Investment Name</th><th>Type</th><th style="text-align:right;">Invested</th><th style="text-align:right;">Current Value</th><th style="text-align:right;">Profit/Loss</th><th style="text-align:right;">Return %</th></tr></thead>
<tbody>${investmentRows}</tbody></table>
<div class="footer"><p><strong>MyInvestFolio</strong> - Your Investment Portfolio Manager</p><p style="margin-top:5px;">Generated on ${new Date().toLocaleString('en-US')}</p></div>
<script>window.onload=function(){window.print()};</script>
</body></html>`;
};


export const buildExcelWorkbook = (investments, portfolioSummary) => {
  const headers = ['Name', 'Type', 'Amount Invested', 'Current Value', 'Profit/Loss', 'Return %', 'Currency', 'Created Date', 'Notes'];
  const rows = investments.map(inv => {
    const profitLoss = (inv.current_value || inv.amount) - inv.amount;
    const returnPct = Number(((profitLoss / inv.amount) * 100).toFixed(2));
    return [
      inv.name || '-', EXPORT_TYPE_LABELS[inv.type] || inv.type,
      inv.amount || 0, inv.current_value || inv.amount || 0,
      Number(profitLoss.toFixed(2)), returnPct, inv.currency || 'USD',
      formatExportDate(inv.created_at), inv.notes || '-'
    ];
  });
  const summaryRow = [
    'TOTAL', '', portfolioSummary?.total_invested || 0,
    portfolioSummary?.current_value || 0, portfolioSummary?.profit_loss || 0,
    Number((portfolioSummary?.profit_loss_percentage || 0).toFixed(2)), '', '', ''
  ];
  const wsData = [headers, ...rows, [], summaryRow];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 30 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Investments');
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
};
